import _ from 'lodash'
import React from 'react'
import { queryFilters } from '../../Queries/queryFilters'
import { qf } from '../../Queries/queryFormatter'
import { addDays, differenceInBusinessDays } from 'date-fns'
import SessionStore from '../../State/SessionStore'
import {
    canViewCostCentres,
    canViewProjectInvoices,
    canViewRoles,
    canViewStaffAllocations,
    canViewStaffChargeOutRate,
    canViewStaffCostRate,
    canViewStaffOvertimeRate,
    canViewStaffPayRate,
} from '../../State/Permissions/HasPermissions'

export const StaffReportColumns = (report) => ({
    name: {
        id: 'name',
        label: 'Name',
        type: 'text',
        width: 25,
        value: (row) => {
            return row.fullName
        },
        component: ({ value, stores }) => {
            const { row } = stores
            return (
                <div>
                    <a
                        href={`/staff/${row.rowObject.id}`}
                        onClick={(e) => {
                            e.preventDefault()
                        }}
                    >
                        {value}
                    </a>
                </div>
            )
        },
    },
    lastName: {
        id: 'lastName',
        label: 'Last name',
        type: 'text',
        width: 15,
        value: (row) => row.lastName,
    },
    firstName: {
        id: 'firstName',
        label: 'First name',
        type: 'text',
        width: 15,
        value: (row) => row.firstName,
    },
    email: {
        id: 'email',
        label: 'Email',
        type: 'text',
        width: 25,
        value: (row) => row.email,
        queryFilters: ({ operator, value }) => {
            return queryFilters.text[operator]('email', value)
        },
    },
    role: {
        id: 'role',
        label: 'Role',
        type: 'role',
        width: 15,
        value: (row) => row.role,
        permissions: (row) => canViewRoles(SessionStore.user),
        queryFilters: ({ operator, value }) => {
            return queryFilters.singleId[operator]('roleId', value)
        },
    },
    costCentre: {
        id: 'costCentre',
        label: 'Cost Centre',
        type: 'costCentre',
        width: 15,
        value: (row) => row.costCentre,
        permissions: (row) => canViewCostCentres(SessionStore.user),
        queryFilters: ({ operator, value }) => {
            return queryFilters.singleId[operator]('costCentreId', value)
        },
    },
    isArchived: {
        id: 'isArchived',
        label: 'Is Archived',
        type: 'boolean',
        width: 15,
        value: (row) => row.isArchived,
        queryFilters: ({ operator, value }) => {
            return queryFilters.boolean[operator]('isArchived', value)
        },
    },
    isActive: {
        id: 'isActive',
        label: 'Is Active',
        type: 'boolean',
        width: 15,
        value: (row) => !row.isArchived,
        queryFilters: ({ operator, value }) => {
            return queryFilters.boolean[operator]('isActive', value)
        },
    },
    payRate: {
        id: 'payRate',
        label: 'Pay Rate',
        type: 'number',
        width: 15,
        value: (row) => row.payRate,
        permissions: (row) => canViewStaffPayRate(SessionStore.user),
        queryFilters: ({ operator, value }) => {
            return queryFilters.number[operator]('payRate', value)
        },
    },
    overtimeRate: {
        id: 'overtimeRate',
        label: 'Overtime Rate',
        type: 'number',
        width: 15,
        value: (row) => row.overtimeRate,
        permissions: (row) => canViewStaffOvertimeRate(SessionStore.user),
        queryFilters: ({ operator, value }) => {
            return queryFilters.number[operator]('overtimeRate', value)
        },
    },
    costRate: {
        id: 'costRate',
        label: 'Cost Rate',
        type: 'number',
        width: 15,
        value: (row) => row.costRate,
        permissions: (row) => canViewStaffCostRate(SessionStore.user),
        queryFilters: ({ operator, value }) => {
            return queryFilters.number[operator]('costRate', value)
        },
    },
    chargeOutRate: {
        id: 'chargeOutRate',
        label: 'Charge Out Rate',
        type: 'number',
        width: 15,
        value: (row) => row.chargeOutRate,
        permissions: (row) => canViewStaffChargeOutRate(SessionStore.user),
        queryFilters: ({ operator, value }) => {
            return queryFilters.number[operator]('chargeOutRate', value)
        },
    },
    weeklyAvailability: {
        id: 'weeklyAvailability',
        label: 'Weekly Availability',
        type: 'number',
        width: 15,
        value: (row) => row.getLatestRate('availability'),
        queryFilters: ({ operator, value }) => {
            return queryFilters.number[operator]('weeklyAvailability', value)
        },
    },
    allocatedHours: {
        id: 'allocatedHours',
        label: 'Allocated hours',
        type: 'number',
        width: 15,
        value: (row) => row.allocatedHours,
        permissions: (row) => canViewStaffAllocations(SessionStore.user),
        queryFilters: ({ operator, value }) => {
            return queryFilters.number[operator]('allocatedHours', value)
        },
    },
    availableHours: {
        id: 'availableHours',
        label: 'Available hours',
        type: 'number',
        width: 15,
        value: (row) => {
            return (
                (row.weeklyAvailability / 5) *
                differenceInBusinessDays(
                    addDays(report.dateRange[1], 1),
                    report.dateRange[0]
                )
            )
        },
        queryFilters: ({ operator, value }) => {
            return queryFilters.number[operator](
                `(weeklyAvailability / 5) * ${differenceInBusinessDays(
                    addDays(report.dateRange[1], 1),
                    report.dateRange[0]
                )}`,
                value
            )
        },
    },
    recordedHours: {
        id: 'recordedHours',
        label: 'Recorded hours',
        type: 'number',
        width: 15,
        value: (row) => row.recordedHours,
        queryFilters: ({ operator, value }) => {
            return queryFilters.number[operator]('recordedHours', value)
        },
    },
    billableHours: {
        id: 'billableHours',
        label: 'Billable Hours',
        type: 'number',
        width: 15,
        value: (row) => row.billableHours,
        queryFilters: ({ operator, value }) => {
            return queryFilters.number[operator]('billableHours', value)
        },
    },
    nonBillableHours: {
        id: 'nonBillableHours',
        label: 'Non Billable Hours',
        type: 'number',
        width: 15,
        value: (row) => row.nonBillableHours,
        queryFilters: ({ operator, value }) => {
            return queryFilters.number[operator]('nonBillableHours', value)
        },
    },
    percentBillableHours: {
        id: 'percentBillableHours',
        label: 'Percent Billable Hours',
        type: 'percent',
        width: 15,
        value: (row) => {
            return (
                StaffReportColumns(report).billableHours.value(row) /
                StaffReportColumns(report).recordedHours.value(row)
            )
        },
        queryFilters: ({ operator, value }) => {
            return queryFilters.number[operator](
                'billableHours / recordedHours',
                value
            )
        },
    },
    overtimeHours: {
        id: 'overtimeHours',
        label: 'Overtime hours',
        type: 'number',
        width: 15,
        value: (row) => row.overtimeHours,
        queryFilters: ({ operator, value }) => {
            return queryFilters.number[operator]('overtimeHours', value)
        },
    },
    invoicedHours: {
        id: 'invoicedHours',
        label: 'Invoiced Hours',
        type: 'number',
        width: 15,
        value: (row) => {
            return row.invoicedHours
        },
        queryFilters: ({ operator, value }) => {
            return queryFilters.number[operator]('invoicedHours', value)
        },
    },
    pay: {
        id: 'pay',
        label: 'Pay',
        type: 'currency',
        width: 15,
        value: (row) => row.pay,
        permissions: (row) => canViewStaffPayRate(SessionStore.user),
        queryFilters: ({ operator, value }) => {
            return queryFilters.number[operator]('pay', value)
        },
    },
    cost: {
        id: 'cost',
        label: 'Cost',
        type: 'currency',
        width: 15,
        value: (row) => row.cost,
        permissions: (row) => canViewStaffCostRate(SessionStore.user),
        queryFilters: ({ operator, value }) => {
            return queryFilters.number[operator]('cost', value)
        },
    },
    chargeOut: {
        id: 'chargeOut',
        label: 'Charge Out',
        type: 'currency',
        width: 15,
        value: (row) => row.chargeOut,
        permissions: (row) => canViewStaffChargeOutRate(SessionStore.user),
        queryFilters: ({ operator, value }) => {
            return queryFilters.number[operator]('chargeOut', value)
        },
    },
    revenueEarned: {
        id: 'revenueEarned',
        label: 'Revenue Earned',
        type: 'currency',
        width: 15,
        value: (row) => {
            return row.revenueEarned
        },
        permissions: (row) =>
            canViewStaffChargeOutRate(SessionStore.user) &&
            canViewProjectInvoices(SessionStore.user),
        queryFilters: ({ operator, value }) => {
            return queryFilters.number[operator]('revenueEarned', value)
        },
    },
    revenueEarnedPerHour: {
        id: 'revenueEarnedPerHour',
        label: 'Revenue Earned Per Hour',
        type: 'currency',
        width: 15,
        value: (row) => {
            return StaffReportColumns(report).recordedHours.value(row)
                ? StaffReportColumns(report).revenueEarned.value(row) /
                      StaffReportColumns(report).recordedHours.value(row)
                : 0
        },
        permissions: (row) =>
            canViewStaffChargeOutRate(SessionStore.user) &&
            canViewProjectInvoices(SessionStore.user),

        queryFilters: ({ operator, value }) => {
            return queryFilters.number[operator](
                'recordedHours > 0 ? revenueEarned / recordedHours : 0',
                value
            )
        },
    },
    projects: {
        id: 'projects',
        label: 'Projects',
        type: 'project',
        width: 15,
        value: (row) => {
            return [...new Set(row.budgets.map((b) => b.project))]
        },
        queryFilters: ({ operator, value }) =>
            queryFilters.manyIds[operator]('projectIds', value),
    },
})
